import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState } from "react";
import Link from "next/link";
import { TextField, Alert, Typography, Box, useTheme, useMediaQuery, FormControlLabel, Checkbox, InputAdornment, IconButton } from "__barrel_optimize__?names=Alert,Box,Checkbox,FormControlLabel,IconButton,InputAdornment,TextField,Typography,useMediaQuery,useTheme!=!@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import { Turnstile } from "@marsidev/react-turnstile";
import { tokens } from "../application/theme";
import { useTranslation } from "../../i18n/client";
import ReusableButton from "../application/components/ReusableButton";
import { signIn } from "next-auth/react";
import { Trans } from "react-i18next";
import { AuthApi } from "../../../apis/auth";
import { isAxiosError } from "axios";
import { appStyled } from "../application/utilityFunctions";
const StyledFormControlLabel = appStyled(FormControlLabel)((param)=>{
    let { checked } = param;
    return {
        "& .MuiFormControlLabel-label": {
            color: "".concat(checked ? "#515151" : "#d32f2f"),
            fontWeight: "500",
            fontSize: "13px"
        }
    };
});
export const SignupForm = (props)=>{
    const [lastName, setLastName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [repeatPassword, setRepeatPassword] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorText, setPasswordErrorText] = useState("");
    const [repeatPasswordError, setRepeatPasswordError] = useState(false);
    const [checked, setChecked] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
    const [termsError, setTermsError] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState(null);
    const [turnstileError, setTurnstileError] = useState(false);
    const turnstileRef = useRef(null);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
    const { t } = useTranslation(props.lng, "signup");
    const resetTurnstile = ()=>{
        var _turnstileRef_current;
        (_turnstileRef_current = turnstileRef.current) === null || _turnstileRef_current === void 0 ? void 0 : _turnstileRef_current.reset();
        setTurnstileToken(null);
    };
    const handleSubmit = async (event)=>{
        event.preventDefault();
        if (isSubmitting) {
            return;
        }
        if (!checked) {
            setTermsError(true);
            setMessage("");
            return;
        }
        if (emailError || passwordError || repeatPasswordError) {
            return;
        }
        if (!turnstileToken) {
            setTurnstileError(true);
            setMessage(t("turnstile_required"));
            setMessageType("error");
            return;
        }
        setMessage("");
        setMessageType("info");
        setTurnstileError(false);
        setIsSubmitting(true);
        try {
            await AuthApi.signUp({
                email,
                password,
                firstName,
                lastName,
                provider: "credentials",
                locale: props.lng,
                turnstileToken
            });
            props.handleEmail(email);
            props.handlePassword(password);
            props.handleVerify(true);
        } catch (error) {
            var _error_response;
            console.error("Signup error:", error);
            let translatedMessage = t("generalErrorMessage");
            resetTurnstile();
            if (isAxiosError(error) && ((_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.data)) {
                const { data } = error.response;
                if ((data === null || data === void 0 ? void 0 : data.code) === "TURNSTILE_FAILED" || error.response.status === 403) {
                    translatedMessage = t("turnstile_failed");
                }
                if (Array.isArray(data === null || data === void 0 ? void 0 : data.data) && data.data.length > 0) {
                    var _data_data__msg, _data_data_;
                    const errorCode = (_data_data_ = data.data[0]) === null || _data_data_ === void 0 ? void 0 : (_data_data__msg = _data_data_.msg) === null || _data_data__msg === void 0 ? void 0 : _data_data__msg.code;
                    if (errorCode === "EMAIL_EXISTS_NOT_VERIFIED") {
                        try {
                            await AuthApi.sendVerificationCode({
                                email,
                                locale: props.lng
                            });
                            props.handleEmail(email);
                            props.handlePassword(password);
                            props.handleVerify(true);
                            return;
                        } catch (resendError) {
                            console.error("Error sending verification code:", resendError);
                        }
                    }
                    if (errorCode === "EMAIL_EXISTS_VERIFIED") {
                        translatedMessage = t("signup_error_exist");
                    }
                }
            }
            setMessage(translatedMessage);
            setMessageType("error");
        } finally{
            setIsSubmitting(false);
        }
    };
    const validatePassword = (password)=>{
        const minLength = 8;
        const hasUppercase = /[A-Z]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecialChar = /[!@#$%^&*]/.test(password);
        if (password.length < minLength) {
            return t("pass_validation_8") //'Password must be at least 8 characters long.';
            ;
        }
        if (!hasUppercase) {
            return t("pass_validation_upper") //'Password must contain at least one uppercase letter.';
            ;
        }
        if (!hasLowercase) {
            return t("pass_validation_lower") //'Password must contain at least one lowercase letter.';
            ;
        }
        if (!hasNumber) {
            return t("pass_validation_number") //'Password must contain at least one number.';
            ;
        }
        if (!hasSpecialChar) {
            return t("pass_validation_character") //'Password must contain at least one special character (!@#$%^&*).';
            ;
        }
        return "";
    };
    const handleChangePassword = (event)=>{
        event.preventDefault();
        setPassword(event.target.value);
        setMessage("");
        const passwordError = validatePassword(event.target.value);
        if (passwordError) {
            setPasswordError(true);
            setPasswordErrorText(passwordError);
        } else {
            setPasswordError(false);
            setPasswordErrorText("");
        }
    };
    const handleChangeRepeatPassword = (event)=>{
        event.preventDefault();
        setRepeatPassword(event.target.value);
        setMessage("");
        setRepeatPasswordError(password !== event.target.value);
    };
    const handleChangeEmail = (event)=>{
        setEmail(event.target.value);
        setMessage("");
        // Check if the email is in the correct format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setEmailError(!emailRegex.test(event.target.value));
    };
    const handleChangeLastName = (event)=>{
        setLastName(event.target.value);
        setMessage("");
    };
    const handleChangeFirstName = (event)=>{
        setFirstName(event.target.value);
        setMessage("");
    };
    const handleGoogleSignUp = async ()=>{
        if (isGoogleSubmitting) {
            return;
        }
        if (checked) {
            setIsGoogleSubmitting(true);
            setMessage("");
            try {
                // Trigger Google login using NextAuth
                await signIn("google", {
                    redirect: true,
                    callbackUrl: "".concat(process.env.NEXT_PUBLIC_CLIENT_URL, "/").concat(props.lng, "/application")
                });
            } catch (error) {
                console.error("Google sign-up error:", error);
                setMessage(t("googleSignupError"));
                setMessageType("error");
            } finally{
                setIsGoogleSubmitting(false);
            }
        } else {
            setTermsError(true);
            setMessage("");
        }
    };
    return /*#__PURE__*/ _jsx(Box, {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        sx: {
            backgroundColor: "#F3F5FD"
        },
        children: /*#__PURE__*/ _jsxs(Box, {
            width: "100%",
            maxWidth: isMobile ? 320 : 501,
            p: "30px",
            borderRadius: "20px",
            sx: {
                backgroundColor: "#FFF"
            },
            children: [
                /*#__PURE__*/ _jsxs(Box, {
                    sx: {
                        display: "grid",
                        gridTemplateColumns: "42px minmax(0, 1fr) 42px",
                        alignItems: "center",
                        gap: "8px",
                        mb: "20px"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(IconButton, {
                            component: Link,
                            href: "/".concat(props.lng),
                            "aria-label": "Back to home",
                            sx: {
                                width: 42,
                                height: 42,
                                color: colors.text.main,
                                backgroundColor: "#FFF",
                                border: "1px solid #DCE3FC",
                                boxShadow: "0 8px 24px rgba(28, 39, 76, 0.08)",
                                "&:hover": {
                                    backgroundColor: "#EEF3FF"
                                }
                            },
                            children: /*#__PURE__*/ _jsx(ArrowBackRounded, {})
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h3",
                            component: "h1",
                            sx: {
                                textAlign: "center",
                                overflowWrap: "break-word"
                            },
                            children: t("createAccount")
                        }),
                        /*#__PURE__*/ _jsx(Box, {})
                    ]
                }),
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit,
                    style: {
                        width: "100%"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(TextField, {
                            label: t("last_name"),
                            type: "text",
                            value: lastName,
                            onChange: handleChangeLastName,
                            variant: "outlined",
                            margin: "normal",
                            fullWidth: true,
                            required: true
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            label: t("first_name"),
                            type: "text",
                            value: firstName,
                            onChange: handleChangeFirstName,
                            variant: "outlined",
                            margin: "normal",
                            fullWidth: true,
                            required: true
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            label: "Email",
                            type: "email",
                            value: email,
                            onChange: handleChangeEmail,
                            variant: "outlined",
                            margin: "normal",
                            fullWidth: true,
                            required: true,
                            error: emailError,
                            helperText: emailError ? t("invalidEmailFormat") : ""
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            label: t("password"),
                            type: showPassword ? "text" : "password",
                            value: password,
                            onChange: handleChangePassword,
                            variant: "outlined",
                            margin: "normal",
                            fullWidth: true,
                            required: true,
                            color: passwordError ? "error" : "success",
                            error: passwordError,
                            helperText: passwordErrorText,
                            InputProps: {
                                endAdornment: /*#__PURE__*/ _jsx(InputAdornment, {
                                    position: "end",
                                    children: /*#__PURE__*/ _jsx(IconButton, {
                                        "aria-label": showPassword ? t("hidePassword") : t("showPassword"),
                                        onClick: ()=>setShowPassword((prev)=>!prev),
                                        onMouseDown: (event)=>event.preventDefault(),
                                        edge: "end",
                                        children: showPassword ? /*#__PURE__*/ _jsx(VisibilityOff, {}) : /*#__PURE__*/ _jsx(Visibility, {})
                                    })
                                })
                            }
                        }),
                        /*#__PURE__*/ _jsx(TextField, {
                            label: t("repeat_password"),
                            type: showRepeatPassword ? "text" : "password",
                            value: repeatPassword,
                            onChange: handleChangeRepeatPassword,
                            variant: "outlined",
                            margin: "normal",
                            fullWidth: true,
                            required: true,
                            color: repeatPasswordError ? "error" : "success",
                            error: repeatPasswordError,
                            helperText: repeatPasswordError ? t("passwordsAreNotEqual") : "",
                            InputProps: {
                                endAdornment: /*#__PURE__*/ _jsx(InputAdornment, {
                                    position: "end",
                                    children: /*#__PURE__*/ _jsx(IconButton, {
                                        "aria-label": showRepeatPassword ? t("hidePassword") : t("showPassword"),
                                        onClick: ()=>setShowRepeatPassword((prev)=>!prev),
                                        onMouseDown: (event)=>event.preventDefault(),
                                        edge: "end",
                                        children: showRepeatPassword ? /*#__PURE__*/ _jsx(VisibilityOff, {}) : /*#__PURE__*/ _jsx(Visibility, {})
                                    })
                                })
                            }
                        }),
                        /*#__PURE__*/ _jsxs(Box, {
                            sx: {
                                mt: 1,
                                mb: 1
                            },
                            children: [
                                /*#__PURE__*/ _jsx(StyledFormControlLabel, {
                                    checked: checked,
                                    control: /*#__PURE__*/ _jsx(Checkbox, {
                                        checked: checked,
                                        disableRipple: true,
                                        onChange: ()=>{
                                            setChecked(!checked);
                                            if (termsError) {
                                                setTermsError(false);
                                                setMessage("");
                                            }
                                        }
                                    }),
                                    label: /*#__PURE__*/ _jsx(Trans, {
                                        i18nKey: "accept_terms_privacy",
                                        t: t,
                                        components: {
                                            link_terms: /*#__PURE__*/ _jsx(Link, {
                                                href: "/".concat(props.lng, "/terms"),
                                                style: {
                                                    color: "inherit"
                                                },
                                                target: "_blank",
                                                rel: "noopener noreferrer"
                                            }),
                                            link_privacy: /*#__PURE__*/ _jsx(Link, {
                                                href: "/".concat(props.lng, "/privacy"),
                                                style: {
                                                    color: "inherit"
                                                },
                                                target: "_blank",
                                                rel: "noopener noreferrer"
                                            })
                                        }
                                    })
                                }),
                                termsError && /*#__PURE__*/ _jsx(Typography, {
                                    variant: "caption",
                                    sx: {
                                        color: "#d32f2f",
                                        ml: 4.5,
                                        mt: 0.5,
                                        display: "block",
                                        fontSize: "0.75rem"
                                    },
                                    children: t("terms_required_error")
                                })
                            ]
                        }),
                        turnstileSiteKey ? /*#__PURE__*/ _jsx(Box, {
                            sx: {
                                mt: 1,
                                mb: 2,
                                display: "flex",
                                justifyContent: "center"
                            },
                            children: /*#__PURE__*/ _jsx(Turnstile, {
                                ref: turnstileRef,
                                siteKey: turnstileSiteKey,
                                options: {
                                    theme: "light",
                                    size: "flexible"
                                },
                                onSuccess: (token)=>{
                                    setTurnstileToken(token);
                                    setTurnstileError(false);
                                    if (messageType === "error") {
                                        setMessage("");
                                    }
                                },
                                onExpire: ()=>{
                                    setTurnstileToken(null);
                                },
                                onError: ()=>{
                                    setTurnstileToken(null);
                                    setTurnstileError(true);
                                    setMessage(t("turnstile_failed"));
                                    setMessageType("error");
                                }
                            })
                        }) : null,
                        turnstileError && /*#__PURE__*/ _jsx(Typography, {
                            variant: "caption",
                            sx: {
                                color: "#d32f2f",
                                mb: 1,
                                display: "block",
                                fontSize: "0.75rem",
                                textAlign: "center"
                            },
                            children: t("turnstile_required")
                        }),
                        /*#__PURE__*/ _jsx(Box, {
                            sx: {
                                opacity: checked ? 1 : 0.7,
                                transition: "opacity 0.2s ease-in-out",
                                position: "relative"
                            },
                            children: /*#__PURE__*/ _jsx(ReusableButton, {
                                name: t("getStarted"),
                                backgroundColor: checked ? colors.button.main : "#cccccc",
                                backgroundColorOnHover: checked ? colors.button.mainOnHower : "#b3b3b3",
                                type: "submit",
                                color: "#FFF",
                                width: "100%",
                                paddingTop: "15px",
                                paddingButtom: "15px",
                                loading: isSubmitting,
                                allowTextWrap: true
                            })
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            component: "h1",
                            style: {
                                color: "#515151",
                                textAlign: "center",
                                marginBottom: "15px",
                                textTransform: "uppercase"
                            },
                            children: t("or")
                        }),
                        /*#__PURE__*/ _jsx(Box, {
                            sx: {
                                opacity: checked ? 1 : 0.7,
                                transition: "opacity 0.2s ease-in-out"
                            },
                            children: /*#__PURE__*/ _jsx(ReusableButton, {
                                name: t("google_registration"),
                                // backgroundColor={colors.button.main} 
                                // backgroundColorOnHover={colors.button.mainOnHower} 
                                startIcon: /*#__PURE__*/ _jsxs("svg", {
                                    xmlns: "http://www.w3.org/2000/svg",
                                    width: "24",
                                    height: "24",
                                    viewBox: "0 0 24 24",
                                    fill: "none",
                                    children: [
                                        /*#__PURE__*/ _jsx("path", {
                                            d: "M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.79 15.71 17.57V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z",
                                            fill: "#4285F4"
                                        }),
                                        /*#__PURE__*/ _jsx("path", {
                                            d: "M11.9997 22.9996C14.9697 22.9996 17.4597 22.0196 19.2797 20.3396L15.7097 17.5696C14.7297 18.2296 13.4797 18.6296 11.9997 18.6296C9.13969 18.6296 6.70969 16.6996 5.83969 14.0996H2.17969V16.9396C3.98969 20.5296 7.69969 22.9996 11.9997 22.9996Z",
                                            fill: "#34A853"
                                        }),
                                        /*#__PURE__*/ _jsx("path", {
                                            d: "M5.84 14.0903C5.62 13.4303 5.49 12.7303 5.49 12.0003C5.49 11.2703 5.62 10.5703 5.84 9.91031V7.07031H2.18C1.43 8.55031 1 10.2203 1 12.0003C1 13.7803 1.43 15.4503 2.18 16.9303L5.03 14.7103L5.84 14.0903Z",
                                            fill: "#FBBC05"
                                        }),
                                        /*#__PURE__*/ _jsx("path", {
                                            d: "M11.9997 5.38C13.6197 5.38 15.0597 5.94 16.2097 7.02L19.3597 3.87C17.4497 2.09 14.9697 1 11.9997 1C7.69969 1 3.98969 3.47 2.17969 7.07L5.83969 9.91C6.70969 7.31 9.13969 5.38 11.9997 5.38Z",
                                            fill: "#EA4335"
                                        })
                                    ]
                                }),
                                borderColor: "#9BC4F5",
                                color: "#282828",
                                width: "100%",
                                onClick: handleGoogleSignUp,
                                loading: isGoogleSubmitting,
                                allowTextWrap: true
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Typography, {
                            component: "p",
                            style: {
                                color: "#515151",
                                textAlign: "center",
                                marginTop: "20px"
                            },
                            children: [
                                t("loginPromptText"),
                                " ",
                                /*#__PURE__*/ _jsx(Link, {
                                    href: "/".concat(props.lng, "/api/auth/login"),
                                    style: {
                                        color: colors.button.main,
                                        fontWeight: 600,
                                        textDecoration: "none"
                                    },
                                    children: t("loginPromptAction")
                                })
                            ]
                        }),
                        message && /*#__PURE__*/ _jsx(Alert, {
                            severity: messageType,
                            style: {
                                marginTop: "20px"
                            },
                            children: message
                        })
                    ]
                })
            ]
        })
    });
};
